import React, { createContext, useEffect, useContext } from 'react';
import { useSnapshot } from 'valtio';
import { sessionState, updateToken } from './sessionState';
import { useLoading } from './LoadingProvider';
import { AuthProvider, AuthContext } from 'react-oauth2-code-pkce';

// Create the context
export const AuthSessionContext = createContext();

// Auth config for react-oauth2-code-pkce
export const authConfig = {
  clientId: import.meta.env.VITE_CLIENT_ID || '8uog5nrra8tpkyp9czq1pg7sq2w1klvv',
  authorizationEndpoint: import.meta.env.VITE_AUTH_ENDPOINT || 'https://stage.adenin.com/oauth2/authorize',
  tokenEndpoint: import.meta.env.VITE_TOKEN_ENDPOINT || 'https://stage.adenin.com/oauth2/token',
  redirectUri: window.location.origin + "/auth-callback",
  decodeToken: false,
  Storage: 'session',
  scope: import.meta.env.VITE_TOKEN_SCOPE || '',
  autoLogin: false,  
  clearURL: false,
  storageKeyPrefix: 'auth_',
  // Optional parameters
  logoutEndpoint: import.meta.env.VITE_OAUTH_LOGOUT_ENDPOINT,
  // PKCE is handled automatically by the library
  onRefreshTokenExpire: (event) => {
    if (window.confirm('Your session has expired. Do you want to login again?')) {
      // encode current url as state to return to after login
      var state = "at::" + window.location.href.replace(window.location.origin,"");
      event.login(encodeURIComponent(btoa(state)));
    }
  },
};

// Wrapper component that combines AuthProvider with our session context
export function AuthSessionProvider({ children }) {
  return (
    <AuthProvider authConfig={authConfig}>
      <AuthSessionProviderInternal>
        {children}
      </AuthSessionProviderInternal>
    </AuthProvider>
  );
}

// Internal component to handle the token management
function AuthSessionProviderInternal({ children }) {
  const snapshot = useSnapshot(sessionState);
  const { startLoading, stopLoading } = useLoading();
  const { token, loginInProgress, error, logIn } = useContext(AuthContext);

  useEffect(() => {       
    if(!token && !loginInProgress) {
      // encode current url as state to return to after login
      var state = "at::" + window.location.href.replace(window.location.origin,"")
      logIn(encodeURIComponent(btoa(state)));
    }

    // If login is in progress, show loading
    if (loginInProgress) {
      sessionState.isLoading = true;
      return;
    }

    // If we have a token, update our session
    if (token) {
      const loaderId = startLoading();

      // Use the function to update token and refresh session
      updateToken(token)
        .finally(() => {
          stopLoading(loaderId);
        });
    } else {
      sessionState.isLoading = false;
    }
  }, [token, loginInProgress, startLoading, stopLoading]);

  // Only render children when not loading
  return (
    <AuthSessionContext.Provider value={{ session: snapshot }}>
      {!snapshot.isLoading && children}
    </AuthSessionContext.Provider>
  );
}